import { ApPayee } from '@/components/ApPayee';
import { DeptSelect } from '@/components/DeptSelect';
import { KeywordCont } from '@/components/KeywordCont';
import { OfficeMember } from '@/components/OfficeMember';
import { Amount } from '@/features/approving_invoice_reports/components/Search/components/Amount';
import { DateRangeSelect } from '@/features/approving_invoice_reports/components/Search/components/DateRangeSelect';
import { FormCheckBox } from '@/features/approving_invoice_reports/components/Search/components/FormCheckBox';
import { FormContainer } from '@/features/approving_invoice_reports/components/Search/components/FormContainer';
import { FromInputSearch } from '@/features/approving_invoice_reports/components/Search/components/FromInputSearch';
import { InvoiceMFFileStatusesSelect } from '@/features/approving_invoice_reports/components/Search/components/InvoiceMFFileStatusesSelect';
import { InvoiceStatusesSelect } from '@/features/approving_invoice_reports/components/Search/components/InvoiceStatusesSelect';
import { useConfigForm } from '@/features/approving_invoice_reports/components/Search/hooks/useConfigForm';
import { SearchFormData } from '@/features/approving_invoice_reports/components/Search/hooks/useSearchForm';
import {
  Span,
  getSearchGridSpans,
} from '@/features/approving_invoice_reports/components/Search/utils/searchGridSpans';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { ButtonV2, Form, Label } from '@moneyforward/ap-frontend-components';
import { Col, Row } from 'antd';
import classnames from 'classnames/bind';
import { FC, PropsWithChildren, memo, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import styles from '../../Search.module.scss';

const cx = classnames.bind(styles);

/** antd Row gutter — same as the Accounting / applicant search forms */
const ROW_GUTTER: [number, number] = [12, 12];

/**
 * NA-5172 — responsive search area (FEATURE_FLAG_APPROVING_INVOICE_REPORTS_RESPONSIVE_SEARCH = ON).
 *
 * Same props as the legacy `SearchForm`; the only difference is the layout:
 * one antd `Row` with a `Col lg / xl / xxl` per field. Breakpoints are the
 * app-wide ones set by the shared ConfigProvider (lg ≥ 1200, xl ≥ 1600,
 * xxl ≥ 1920), so the Row wraps into 4 / 3 / 2 rows. The legacy form is
 * rendered when the flag is OFF (see `Search/index.tsx`).
 */
export interface ResponsiveSearchFormProps
  extends Pick<
    ReturnType<typeof useConfigForm>,
    | 'customProps'
    | 'checkLengthValidationRules'
    | 'deptsExisted'
    | 'reportDeptsSetting'
  > {
  form: UseFormReturn<SearchFormData>;
  onSubmit: (data: SearchFormData) => void;
  onReset: () => void;
  deptOptions: BaseOption[];
  memberOptions: BaseOption[];
  appPayeeDefaultOptions: BaseOption[];
  reportDeptDefaultOptions: BaseOption[];
  isLoading?: boolean;
}

/**
 * One grid cell. The base `span` equals the lg span so viewports below
 * 1200 px keep the lg layout instead of collapsing to content width.
 */
const Cell: FC<PropsWithChildren<{ span: Span }>> = ({ span, children }) => (
  <Col span={span.lg} {...span}>
    <FormContainer>{children}</FormContainer>
  </Col>
);
Cell.displayName = 'ResponsiveSearchFormCell';

export const ResponsiveSearchForm: FC<ResponsiveSearchFormProps> = memo(
  ({
    form,
    onSubmit,
    onReset,
    deptOptions,
    memberOptions,
    customProps,
    checkLengthValidationRules,
    deptsExisted,
    reportDeptsSetting,
    appPayeeDefaultOptions,
    reportDeptDefaultOptions,
    isLoading = false,
  }) => {
    const { t } = useTranslation();
    const { control } = form;

    const showDept = !!deptsExisted?.enabled;
    const showReportDept = showDept && !!reportDeptsSetting?.enabled;
    const spans = useMemo(
      () => getSearchGridSpans({ showDept, showReportDept }),
      [showDept, showReportDept]
    );

    return (
      <Form<SearchFormData> formMethod={form} onSubmit={onSubmit}>
        {/* one 24-column grid; the Row wraps so lg → 4 rows, xl → 3 rows, xxl → 2 rows */}
        <Row gutter={ROW_GUTTER} className={cx('search-grid')}>
          {/* keyword */}
          <Cell span={spans.query}>
            <div>
              <Label
                text={t('business_document_key_word')}
                tooltip={t('common.search.keyword_tooltip_prefix_match')}
              />
              <FromInputSearch
                control={control}
                inputProps={customProps?.inputProps?.query}
                name='query'
                inputId='query'
                rules={checkLengthValidationRules}
                testId='query-input'
              />
            </div>
          </Cell>
          {/* report number */}
          <Cell span={spans.number}>
            <KeywordCont
              control={form.control}
              name='number'
              labelProps={customProps?.labelProps?.number}
              inputProps={customProps?.inputProps?.number}
              rules={checkLengthValidationRules}
            />
          </Cell>
          {/* application status */}
          <Cell span={spans.status}>
            <div className={cx('select-wrapper')}>
              <label className={cx('label-text')}>
                {t(
                  'approving_invoice_reports.table.columns.application_status'
                )}
              </label>
              <InvoiceStatusesSelect control={control} name='status' />
            </div>
          </Cell>
          {/* payee */}
          <Cell span={spans.ap_payee_id}>
            <div className={cx('select-wrapper-app-payee')}>
              <label className={cx('label-text')}>{t('ap_payee')}</label>
              <ApPayee<SearchFormData>
                control={control}
                name='ap_payee_id'
                inputId='payee'
                formContext={undefined}
                defaultOptions={appPayeeDefaultOptions}
                selectProps={customProps?.selectProps?.configApPayee}
              />
            </div>
          </Cell>
          {/* total amount — `amount-range` lets both inputs share the cell width */}
          <Cell span={spans.total_amount}>
            <label className={cx('label-text')}>{t('total_amount')}</label>
            <div className={cx('amount-range')}>
              <Amount control={control} name='total_amount_from' />
              <span className={cx('amount-range-separator')}>~</span>
              <Amount control={control} name='total_amount_to' />
            </div>
          </Cell>
          {/* applicant (office member) */}
          <Cell span={spans.office_member_ids}>
            {/* label outside `select-wrapper` like the department fields: no extra label margin */}
            <label className={cx('label-text')}>
              {t('applicant_office_member')}
            </label>
            <div className={cx('select-wrapper')}>
              <OfficeMember<SearchFormData>
                control={control}
                name='office_member_ids'
                inputId='applicant'
                selectProps={customProps?.selectProps?.config}
                defaultOptions={memberOptions ?? []}
              />
            </div>
          </Cell>
          {/* applicant department */}
          {showDept && (
            <Cell span={spans.dept_ids}>
              <label className={cx('label-text')}>
                {t('applicant_department')}
              </label>
              <div className={cx('select-wrapper')}>
                <DeptSelect<SearchFormData>
                  mode='multiple'
                  control={control}
                  name='dept_ids'
                  inputId='applicantDepartment'
                  selectProps={
                    customProps?.selectProps?.configApplicantDepartment
                  }
                  defaultOptions={deptOptions ?? []}
                />
              </div>
            </Cell>
          )}
          {/* report department */}
          {showReportDept && (
            <Cell span={spans.report_dept_ids}>
              <label className={cx('label-text')}>
                {t('selected_department')}
              </label>
              <div className={cx('select-wrapper')}>
                <DeptSelect<SearchFormData>
                  mode='multiple'
                  control={control}
                  name='report_dept_ids'
                  inputId='selectedDepartment'
                  selectProps={customProps?.selectProps?.configDept}
                  defaultOptions={reportDeptDefaultOptions}
                />
              </div>
            </Cell>
          )}
          {/* attached invoice */}
          <Cell span={spans.mf_file}>
            <div className={cx('select-wrapper')}>
              <label className={cx('label-text')}>
                {t('attached_invoice')}
              </label>
              <InvoiceMFFileStatusesSelect
                control={control}
                name='mf_file'
                formContext={form}
              />
            </div>
          </Cell>
          {/* dates */}
          <Cell span={spans.created_date}>
            <DateRangeSelect
              control={control}
              name='created_date'
              labelProps={customProps?.labelProps?.createdDate}
            />
          </Cell>
          <Cell span={spans.submitted_date}>
            <DateRangeSelect
              control={control}
              name='submitted_date'
              labelProps={customProps?.labelProps?.submittedDate}
            />
          </Cell>
          <Cell span={spans.approved_date}>
            <DateRangeSelect
              control={control}
              name='approved_date'
              labelProps={customProps?.labelProps?.approvedDate}
            />
          </Cell>
          <Cell span={spans.book_date}>
            <DateRangeSelect
              control={control}
              name='book_date'
              labelProps={customProps?.labelProps?.bookDate}
            />
          </Cell>
          <Cell span={spans.due_date}>
            <DateRangeSelect
              control={control}
              name='due_date'
              labelProps={customProps?.labelProps?.dueDate}
            />
          </Cell>
          {/* 代理承認除外 */}
          <Cell span={spans.without_agent}>
            <div className={cx('checkbox-wrapper')}>
              <FormCheckBox
                control={control}
                name='without_agent'
                labelProps={customProps?.labelProps?.withoutAgent}
              />
            </div>
          </Cell>
        </Row>

        {/* Action Buttons */}
        <div className={cx('action-buttons')}>
          <ButtonV2
            size='sm'
            isTertiary
            onClick={onReset}
            className={cx('reset-button')}
          >
            {t('reset')}
          </ButtonV2>
          <ButtonV2
            size='sm'
            isSecondary
            type='submit'
            className={cx('search-button')}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {t('search')}
          </ButtonV2>
        </div>
      </Form>
    );
  }
);
ResponsiveSearchForm.displayName = 'ResponsiveSearchForm';
